import AppStore from "@/store/AppStore"
import dayjs, { Dayjs } from "dayjs"
import Compressor from "compressorjs"
import { getAnalytics, logEvent } from "firebase/analytics"
import { toJS } from "mobx"
import { ImageType } from "../../interface/IImageUpload"

var GenerateNick = (nick?: string) => {
  if (nick) {
    if (nick.length > 2) {
      nick = nick.replace(/[^a-zA-Z0-9\s]/g, "")

      if (nick.includes(" ")) {
        var split = nick?.split(" ")
        nick = ""
        split.map((x) => (nick += x.charAt(0)))
      }
      nick = nick.substring(0, 2)
    }
    return nick.toUpperCase()
  } else return ""
}

const debounce = (func: any, delay: number) => {
  let timerId: any
  return function (...args: any) {
    if (timerId) {
      clearTimeout(timerId)
    }
    timerId = setTimeout(() => {
      // @ts-ignore
      func.apply(this, args)
      timerId = null
    }, delay)
  }
}

const EventLogger = (eventName: string, options?: Record<string, any>) => {
  try {
    const analytics = getAnalytics()
    logEvent(analytics, eventName, {
      ...toJS(AppStore.userDetails),
      timestamp: dayjs().unix() * 1000,
      ...options,
    })
  } catch { }
}

const dynamicDropdownHash: Record<string, any> = {
  "additional-service/internal-employees": {
    label: "Full_Name",
    value: "Full_Name",
  },
  "cleanerapp/cleaning_companies_manager": {
    label: "Full_Name",
    value: "id",
  },
  "property_reservationDetails/property_replacement_types": {
    label: "Replacement_Type",
    value: "Replacement_Type",
  },
  "property_reservationDetails/replacement_items": {
    label: "item",
    value: "item",
    sub_value: "sell_price",
  },
  "additional-service/assigned-employees": {
    label: "Full_Name",
    value: "Full_Name",
  },
  "ticket/company_list": {
    label: "name",
    value: "id",
    sub_value: "name",
  },
  "ticket/company_user_list": {
    label: "Full_Name",
    value: "id",
    sub_value: "Full_Name",
  },
  "cleans/get_priority": {
    label: "value",
    value: "value",
  },
}

function checkFileSize(max: number) {
  return function checkFilesForSize(files?: unknown | File | [File]): boolean {
    let valid = true
    if (files) {
      if (!Array.isArray(files)) {
        files = [files]
      }
      if (Array.isArray(files)) {
        files.every((file) => {
          const size = file.size / 1024
          if (size > max) {
            valid = false
          }
          return valid
        })
      }
    }
    return valid
  }
}

function isObjectEmpty(obj: Record<string, unknown>): boolean {
  return Object.keys(obj).length === 0
}

function isStringParsable(str: string): boolean {
  try {
    JSON.parse(str)
    return true
  } catch {
    return false
  }
}

function downloadFile(url: string) {
  const download = url.split("/").pop()
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("target", "_blank")
  link.setAttribute("download", `${download}.pdf`)
  document.body.appendChild(link)
  link.click()
  link.remove()
}

const blobToFile = (blob: Blob, fileName: string) => {
  return new File([blob], fileName, {
    type: blob.type,
    lastModified: Date.now(),
  })
}

const compressFile = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: 0.4,
      success: (result: Blob) => {
        resolve(blobToFile(result, result.name))
      },
      error: (err: Error) => {
        reject(err)
      },
    })
  })
}

const extractImageUrl = (image: ImageType): string => {
  return typeof image === "string" ? image : image.url
}

const extractImageTimeStamp = (image: ImageType): string | undefined => {
  return typeof image === "string" ? undefined : dayjs(image.uploaded_at).format("DD-MM-YYYY")
}

const responseToImagePreview = (image: ImageType): { url: string; title?: string } => {
  return typeof image === "string" ? { url: image } : { url: image.url, title: dayjs(image.uploaded_at).format("DD-MM-YYYY hh:mm A") }
}

function isValidURL(url: string) {
  try {
    new URL(url)
    return true
  } catch (e) {
    return false
  }
}

const normalizeDateValue = (value: string | number | undefined): Dayjs | null => {
  if (!value) return null

  //"DD-MM-YYYY" format
  if (typeof value === "string" && /^\d{2}-\d{2}-\d{4}$/.test(value)) {
    return dayjs(value, "DD-MM-YYYY")
  }

  //Unix timestamp string
  if (typeof value === "string" && /^\d+$/.test(value)) {
    return dayjs(Number(value))
  }

  // Unix timestamp number
  if (typeof value === "number") {
    return dayjs(value)
  }

  // Other
  return null
}

const getGstCalculated = (totalAmt: number, gstPercentage: number) => {
  return Number((totalAmt * gstPercentage) / (100 + gstPercentage)).toFixed(2)
}

export {
  isValidURL,
  GenerateNick,
  debounce,
  EventLogger,
  isObjectEmpty,
  blobToFile,
  dynamicDropdownHash,
  compressFile,
  checkFileSize,
  isStringParsable,
  downloadFile,
  extractImageUrl,
  extractImageTimeStamp,
  responseToImagePreview,
  normalizeDateValue,
  getGstCalculated
}
